import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, Observable } from 'rxjs';
import { Prescription } from '@models/prescription';
import { Patient } from '@models/patient';
import { PrescriptionForm } from '@classes/prescription-form';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { PrescriptionService } from '@services/prescription.service';
import { SettingsService } from '@services/settings.service';
import { RX_STATUS_OPTIONS } from '../../../../constants/rx-status';
import {
  RX_NAME_SUGGESTIONS,
  formatRxUseLabel,
  rxDoctorName,
  rxExpirationDate,
  rxStatusLabel,
} from '../../../../constants/rx-names';
import { CallRxDialogComponent } from '@components/admin/order/task-popups/call-rx-dialog.component';
import { RxHistoryDialogComponent } from '@components/admin/order/task-popups/rx-history-dialog.component';
import { toggleJobAction } from '@classes/job-actions';

@Component({
  selector: 'app-prescription-details',
  templateUrl: './prescription-details.component.html',
  styleUrls: ['./prescription-details.component.css', '../order.component.css'],
})
export class PrescriptionDetailsComponent implements OnInit, OnDestroy {
  @Input() orderForm: FormGroup;
  @Input() patient: Patient;
  @Output() saveOrder = new EventEmitter<{ destination: string }>();

  formGroup: FormGroup;
  rxStatusOptions = RX_STATUS_OPTIONS;
  rxNameSuggestions = RX_NAME_SUGGESTIONS;
  rxNameControl = new FormControl('');
  patientPrescriptions: Prescription[] = [];
  sourceImageUrl = '';
  extractedUnverified = false;
  verified = false;
  loadingHistory = false;
  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private auth: AuthService,
    private notificationService: NotificationService,
    private prescriptionService: PrescriptionService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    this.bindPrescriptionForm(
      PrescriptionForm.initPrescriptionFormGroup(new Prescription())
    );
    this.rxNameControl.setValue(this.formGroup.get('type')?.value || '');
    this.loadPatientPrescriptions();
    const savedId = String(this.orderForm?.get('prescriptionId')?.value || '');
    const savedName = String(this.orderForm?.get('rxName')?.value || '');
    if (savedName) {
      this.rxNameControl.setValue(savedName);
    }
    if (savedId) {
      this.formGroup.get('_id')?.setValue(savedId);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get activePrescriptions(): Prescription[] {
    return (this.patientPrescriptions || []).filter(
      (rx) => rxStatusLabel(rx) === 'ACTIVE'
    );
  }

  get filteredRxNames(): string[] {
    const query = String(this.rxNameControl.value || '').toUpperCase();
    const names = new Set(this.rxNameSuggestions);
    this.patientPrescriptions.forEach((rx) => {
      const name = String(rx.type || '').trim();
      if (name) {
        names.add(name);
      }
    });
    return Array.from(names).filter((name) => !query || name.includes(query));
  }

  rxUseLabel(rx: Prescription): string {
    return formatRxUseLabel(rx);
  }

  onSelectExistingRx(prescriptionId: string): void {
    if (!prescriptionId) {
      this.formGroup.get('_id')?.setValue('');
      this.orderForm?.get('prescriptionId')?.setValue('');
      return;
    }
    const rx = this.patientPrescriptions.find(
      (item) => String(item._id) === String(prescriptionId)
    );
    if (!rx) {
      return;
    }
    this.applyPrescription(rx);
  }

  openRxHistory(): void {
    this.dialog
      .open(RxHistoryDialogComponent, {
        width: '640px',
        data: { prescriptions: this.patientPrescriptions },
      })
      .afterClosed()
      .subscribe((rx) => {
        if (rx) {
          this.applyPrescription(rx);
        }
      });
  }

  onRxToComeChange(value: string): void {
    if (value === 'CALL FOR RX') {
      this.promptCallRx();
      return;
    }
    if (value === 'PT. TO SUPPLY') {
      this.applyPtToSupplyToOrder();
    }
  }

  onUploadSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.sourceImageUrl = String(reader.result || '');
      this.extractedUnverified = true;
      this.verified = false;
      this.notificationService.show(
        'success',
        'RX uploaded. Review the original and verify the values before saving.',
        'OK'
      );
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  onSave(destination: string): void {
    if (destination === 'VOID') {
      this.saveOrder.emit({ destination });
      return;
    }
    if (this.extractedUnverified && !this.verified) {
      this.notificationService.show(
        'error',
        'Verify the original RX before saving extracted or uploaded prescription data.',
        'OK'
      );
      return;
    }
    const rxStatus = String(this.orderForm?.get('rxStatus')?.value || '');
    this.syncRxOntoOrder();
    this.persistPrescriptionRecord(() => {
      if (rxStatus === 'CALL FOR RX') {
        this.applyCallForRxToOrder();
      } else if (rxStatus === 'PT. TO SUPPLY') {
        this.applyPtToSupplyToOrder();
      }
      this.saveOrder.emit({ destination: 'SAVE' });
    });
  }

  private loadPatientPrescriptions(): void {
    const patientId = String(this.patient?._id || '').trim();
    if (!patientId) {
      return;
    }
    this.loadingHistory = true;
    this.prescriptionService.getPatientPrescriptions(patientId).subscribe({
      next: (prescriptions) => {
        this.patientPrescriptions = prescriptions || [];
        this.loadingHistory = false;
        const savedId = String(this.orderForm?.get('prescriptionId')?.value || '');
        const match = this.patientPrescriptions.find(
          (rx) => String(rx._id) === savedId
        );
        if (match && !this.hasStructuredValues()) {
          this.applyPrescription(match);
        }
      },
      error: () => {
        this.loadingHistory = false;
      },
    });
  }

  private applyPrescription(rx: Prescription): void {
    const next = PrescriptionForm.initPrescriptionFormGroup(rx);
    this.bindPrescriptionForm(next);
    this.rxNameControl.setValue(rx.type || '', { emitEvent: false });
    this.orderForm?.get('prescriptionId')?.setValue(rx._id);
    this.orderForm?.get('rxName')?.setValue(rx.type || '');
    this.extractedUnverified = false;
    this.verified = true;
    const images = rx.images || [];
    const first = images[0] as any;
    this.sourceImageUrl =
      String((rx as any).sourceImageDataUrl || '').trim() ||
      first?.url ||
      first?.dataUrl ||
      '';
    this.syncExpirationDate();
  }

  private bindPrescriptionForm(form: FormGroup): void {
    if (!form.get('hide')) {
      form.addControl('hide', this.fb.control(false));
    }
    if (!form.get('doctor')) {
      form.addControl('doctor', this.fb.control(''));
    }
    this.formGroup = form;
    const doctor = String(form.get('doctor')?.value || '').trim() || rxDoctorName(form.getRawValue());
    form.get('doctor')?.setValue(doctor, { emitEvent: false });
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.subscriptions.add(
      this.rxNameControl.valueChanges.subscribe((value) => {
        this.formGroup.get('type')?.setValue(String(value || '').trim());
      })
    );
    this.subscriptions.add(
      this.formGroup.get('examDate')?.valueChanges.subscribe(() => {
        this.syncExpirationDate();
      })
    );
    this.subscriptions.add(
      this.formGroup.get('oneYearExpiration')?.valueChanges.subscribe(() => {
        this.syncExpirationDate();
      })
    );
  }

  private syncExpirationDate(): void {
    const exam = this.formGroup.get('examDate')?.value;
    const oneYear = !!this.formGroup.get('oneYearExpiration')?.value;
    const expires = rxExpirationDate(exam, oneYear);
    if (expires) {
      this.formGroup.get('expirationDate')?.enable({ emitEvent: false });
      this.formGroup.get('expirationDate')?.setValue(expires, { emitEvent: false });
      this.formGroup.get('expirationDate')?.disable({ emitEvent: false });
    }
  }

  private syncRxOntoOrder(): void {
    const name = String(this.rxNameControl.value || this.formGroup.get('type')?.value || '').trim();
    this.formGroup.get('type')?.setValue(name);
    this.orderForm?.get('rxName')?.setValue(name);
    this.orderForm?.get('prescriptionId')?.setValue(
      this.formGroup.get('_id')?.value || ''
    );
  }

  private persistPrescriptionRecord(done: () => void): void {
    const patientId = String(this.patient?._id || '').trim();
    if (!patientId) {
      done();
      return;
    }
    const payload: any = {
      ...this.formGroup.getRawValue(),
      patientId,
      hide: !!this.formGroup.get('hide')?.value,
      doctor: this.formGroup.get('doctor')?.value || '',
      sourceImageDataUrl: this.sourceImageUrl || '',
      extractedUnverified: this.extractedUnverified && !this.verified,
      verifiedBy: this.verified
        ? String(this.orderForm?.get('initials')?.value || '').trim()
        : '',
      verifiedAt: this.verified ? new Date() : null,
    };
    if (!payload._id) {
      delete payload._id;
    }
    delete payload.html;
    delete payload.eyeExam;
    if (!Array.isArray(payload.images) || !payload.images[0]) {
      delete payload.images;
    }
    const existingId = String(payload._id || '').trim();
    const request$: Observable<any> = existingId
      ? this.prescriptionService.updatePrescription(existingId, payload)
      : this.prescriptionService.addPrescription(payload);
    request$.subscribe({
      next: (saved: any) => {
        const id = saved?._id || saved?.prescriptionId || existingId;
        if (id) {
          this.formGroup.get('_id')?.setValue(id);
          this.orderForm?.get('prescriptionId')?.setValue(id);
        }
        this.loadPatientPrescriptions();
        done();
      },
      error: () => {
        this.notificationService.show(
          'error',
          'Could not save the RX record. The order was not updated.',
          'OK'
        );
      },
    });
  }

  private applyPtToSupplyToOrder(): void {
    const actions = toggleJobAction(
      this.orderForm?.get('jobActions')?.value || [],
      'CALL FOR RX',
      false
    );
    this.orderForm?.get('jobActions')?.setValue(actions);
    if (this.orderForm?.get('jobAction')?.value === 'CALL FOR RX') {
      this.orderForm?.get('jobAction')?.setValue('');
    }
    if (this.orderForm?.get('status')?.value === 'CALL FOR RX') {
      this.orderForm?.get('status')?.setValue('');
    }
    if (this.orderForm?.get('todo')?.value === 'CALL') {
      this.orderForm?.get('todo')?.setValue('');
    }
    this.orderForm?.get('rxStatus')?.setValue('PT. TO SUPPLY');
  }

  private applyCallForRxToOrder(): void {
    const actions = toggleJobAction(
      this.orderForm?.get('jobActions')?.value || [],
      'CALL FOR RX',
      true
    );
    this.orderForm?.get('jobActions')?.setValue(actions);
    this.orderForm?.get('jobAction')?.setValue('CALL FOR RX');
    this.orderForm?.get('status')?.setValue('CALL FOR RX');
    this.orderForm?.get('todo')?.setValue('CALL');
    this.orderForm?.get('rxStatus')?.setValue('CALL FOR RX');
  }

  private promptCallRx(): void {
    this.dialog
      .open(CallRxDialogComponent, {
        width: '420px',
        data: {
          whoToCallOptions: this.settingsService.activeWhoToCallOptions(),
          date:
            this.orderForm?.get('products.0.frame.followUpDate')?.value ||
            new Date(),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          this.orderForm?.get('rxStatus')?.setValue('');
          return;
        }
        if (result.date) {
          this.orderForm?.get('products.0.frame.followUpDate')?.setValue(result.date);
        }
        this.applyCallForRxToOrder();
        const activity = this.orderForm?.get('products.0.frame.activity') as FormArray;
        if (activity) {
          activity.push(
            this.fb.group({
              id: '',
              kind: 'open',
              date: result.date || new Date(),
              status: 'CALL / RX',
              branch: '',
              notes: result.notes || '',
              repairItems: [[]],
              waitingOn: result.whoToCall || '',
              vendor: '',
              followUpDate: result.date || '',
              expectedDate: '',
              followUpNumber: 0,
              activityChoice: '',
              reopenedFrom: '',
              sourceId: '',
              description: '',
              referenceNumber: '',
              carrier: '',
              trackingNumber: '',
              labVendor: '',
              repairLocation: '',
              estimate: '',
              estimateApprovalRequired: false,
              approvalStatus: '',
              otherText: '',
              whoToCall: result.whoToCall || '',
              images: [[]],
              eta: '',
              completed: false,
            })
          );
        }
        if (result.notes) {
          const notes = String(this.formGroup.get('notes')?.value || '');
          if (!notes) {
            this.formGroup.get('notes')?.setValue(result.notes);
          }
        }
      });
  }

  private hasStructuredValues(): boolean {
    const right = this.formGroup?.get('right')?.value || {};
    const left = this.formGroup?.get('left')?.value || {};
    return !!(right.sph || right.cyl || left.sph || left.cyl);
  }
}
